import { useQuery } from '@apollo/client';
import { Skeleton } from '@material-ui/lab';
import type { FC } from 'react';
import { useEffect, useState } from 'react';

import { MarketplaceComponent } from '../../../components/marketplace/Marketplace.component';
import { MarketplaceListV2 } from '../../../components/marketplace/MarketplaceListV2.component';
import { SearchBar } from '../../../components/search-bar/SearchBar.component';
import { useWeb3State } from '../../../hooks';
import { featureBondedQuery } from '../../../hooks/jim-hook';
import type { Asset } from '../../../lib/graph/MogulMarketplace';
import {
  featuredAssetListQuery,
  generateFeaturedListQueryTokenAddresses,
} from '../../../lib/graph/queries/featuredAssetListQuery';
import {
  generateTrendingListQueryTokenAddresses,
  trendingAssetsQuery,
} from '../../../lib/graph/queries/trendingAssetsQuery';
import styles from './MarketplacePage.module.css';

export const MarketplacePage: FC = () => {
  const { walletDetails } = useWeb3State();
  const [featuredNfts, setFeaturedNfts] = useState<Asset[]>([]);
  const [trendingNfts, setTrendingNfts] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);

  const trendingQuery = useQuery<{ assets: Asset[] }>(trendingAssetsQuery, {
    variables: { addresses: [] },
  });

  const featuredQuery = useQuery<{ assets: Asset[] }>(featuredAssetListQuery, {
    variables: { addresses: [] },
  });

  const { data: bondedData } = useQuery<{ assets: Asset[] }>(
    featureBondedQuery,
    {
      context: { forceFetch: true, bonded: true, queryDeduplication: false },
      fetchPolicy: 'no-cache',
    },
  );

  useEffect(() => {
    if (!bondedData) {
      return;
    }

    void (async () => {
      const {
        data: { assets: featuredAssets },
      } = await featuredQuery.fetchMore({
        query: featuredAssetListQuery,
        variables: await generateFeaturedListQueryTokenAddresses(
          walletDetails.chain.type,
        ),
      });

      setFeaturedNfts(featuredAssets);
      try {
        const {
          data: { assets: trendingData },
        } = await trendingQuery.fetchMore({
          query: trendingAssetsQuery,
          variables: await generateTrendingListQueryTokenAddresses(
            walletDetails.chain.type,
          ),
        });

        setTrendingNfts(trendingData);
      } catch (e) {
        setTrendingNfts([] as Asset[]);
      }
      setLoading(false);
    })();
  }, [bondedData]);

  // </JIM GLUE>

  const [search, setSearch] = useState<string>('');

  return (
    <div
      className={`flex flex-col items-center h-auto bg-container bg-cover bg-repeat ${styles.container}`}
    >
      <div className="max-w-screen-lg mx-0 sm:w-11/12 w-full">
        <div className="flex flex-row justify-between items-center">
          <h1
            className={`${styles.headers} sm:mt-6 mt-14 sm:mb-0 mb-1  sm:ml-0 ml-5`}
          >
            Marketplace
          </h1>
        </div>
        <SearchBar tailwindOverride={'sm:mt-2 mb-4'} setSearch={setSearch} />
        {featuredNfts.length > 0 && (
          <div className="fontVanguard font-bold xl:text-4xl text-3xl mt-8 mb-4 sm:ml-0 ml-5">
            Featured Digital Collectibles
          </div>
        )}
        {featuredNfts.length > 0 && (
          <MarketplaceComponent
            isFeaturedOrTrending={true}
            assets={featuredNfts}
          />
        )}
        {trendingNfts.length > 0 && (
          <div className="fontVanguard font-bold xl:text-4xl text-3xl mt-8 mb-4 sm:ml-0 ml-5">
            Trending Digital Collectibles
          </div>
        )}
        {trendingNfts.length > 0 && (
          <MarketplaceComponent
            isFeaturedOrTrending={true}
            assets={trendingNfts}
          />
        )}
        {loading ? (
          <div className="grid xl:grid-cols-4 lg:grid-cols-3 md:grid-cols-2 grid-cols-1">
            {Array.from(Array(10).keys()).map(i => (
              <Skeleton height={550} width={250} key={i} />
            ))}
          </div>
        ) : (
          <MarketplaceListV2 search={search} />
        )}
      </div>
    </div>
  );
};
